import {
  Alert,
  Box,
  Button,
  capitalize,
  FormControl,
  FormLabel,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import { MedicationType } from "src/components/PrescriptionRequest/PrescriptionRequest";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DosageInstructions, MedicationForm } from "src/api/types";
import { usePrescription } from "src/context/PrescriptionProvider";
import { theme } from "src/assets/theme";
import { useCreateOrderMutation } from "src/api/services/ordersApi";
import PrescriptionInputs from "./components/PrescriptionInputs";
import { useUpdatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import { isTruthy } from "src/utils/isTruthy";
import { getTotalDispenseQuantity } from "./utils/getTotalDispenseQuantity";
import { useGetSuppliesQuery } from "src/api/services/medicationsApi";
import { usePrescribe } from "src/context/PrescribeProvider/usePrescribe";

type PrescribeModalTypes = {
  open: boolean;
  onClose: (o: boolean) => void;
  medication: MedicationType;
  daysSupply?: number;
  onSuccess: () => void;
  medicationOptions: MedicationType[];
};

export type Vial = {
  medicationId: number | null;
  instruction: {
    amount: string;
    dosage: string;
    duration: string;
    value: string;
    interval: string;
    interval_count: number;
  } | null;
  supplyId: number | null;
  pharmacyId: number;
  dispense_quantity: number | null;
};

const mapAmountToCategory: Record<string, string> = {
  "Hair Loss": "3 drops",
};

const PrescribeModal = ({
  open,
  onClose,
  medication,
  daysSupply,
  onSuccess,
  medicationOptions,
}: PrescribeModalTypes) => {
  const { data: supplies = [] } = useGetSuppliesQuery({
    pharmacy_id: medication.pharmacyId,
    active: true,
  });
  const { durationInDays, quantity, setQuantity } = usePrescribe();

  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const patientId = usePrescription((p) => p.patient_id);
  const prescriptionId = usePrescription((p) => p.id);
  const productId = usePrescription((p) => p.product_id);
  const billingModel = usePrescription((p) => p.product?.billing_model);
  const prescriptionType = usePrescription((p) => p.type);
  const dispenseQuantity = usePrescription((p) =>
    Number(p.dispense_quantity ?? "1"),
  );
  const category = usePrescription((p) => p.product_category);
  const invoiceId = usePrescription(
    (p) => (p.metadata as Record<string, string>)?.invoice_id,
  );
  const refills = usePrescription((p) => {
    if (p.refills == null) return 0;
    return p.refills;
  });
  const [updatePrescription] = useUpdatePrescriptionMutation();
  const [createOrder] = useCreateOrderMutation();
  const [vials, setVials] = useState<Vial[]>([]);
  const [supplyId, setSupplyId] = useState<number | null>(
    supplies[0]?.id ? supplies[0]?.id : null,
  );
  const isDispenseUnitVial = useMemo(
    () => medication.dispense_unit === "vial",
    [medication.dispense_unit],
  );

  const setFirstVial = useCallback((): Vial[] => {
    const arrayLength = Math.min(dispenseQuantity, 3);

    return Array.from({ length: arrayLength }, (_, i) =>
      i === 0
        ? {
            medicationId: medication.id,
            instruction: null,
            supplyId: isDispenseUnitVial ? supplyId : null,
            pharmacyId: medication.pharmacyId,
            dispense_quantity: medication.dispense_quantity,
          }
        : {
            medicationId: null,
            instruction: null,
            supplyId: isDispenseUnitVial ? supplyId : null,
            pharmacyId: medication.pharmacyId,
            dispense_quantity: null,
          },
    );
  }, [
    dispenseQuantity,
    isDispenseUnitVial,
    medication.dispense_quantity,
    medication.id,
    medication.pharmacyId,
    supplyId,
  ]);

  useEffect(() => {
    setVials(setFirstVial());
    if (medication != null && (quantity == null || quantity === 0)) {
      setQuantity(medication.dispense_quantity);
    }
  }, [medication, quantity, setFirstVial, setQuantity]);

  const handleVial = useCallback(
    (idx: number, changes: Partial<Vial>) =>
      setVials((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...changes };
        return next;
      }),

    [],
  );

  const handleInstruction = useCallback(
    (idx: number, instructions: DosageInstructions[]) =>
      (e: ChangeEvent<HTMLInputElement>) => {
        const selectedInstruction = instructions.find(
          (i) => i.instruction.value === e.target.value,
        );

        const dispenseQuantity = selectedInstruction?.dispense_quantity;

        handleVial(idx, {
          instruction: {
            amount:
              mapAmountToCategory[category.name] ??
              selectedInstruction?.instruction.amount,
            dosage:
              mapAmountToCategory[category.name] ??
              selectedInstruction?.instruction.amount,
            duration: `${daysSupply} days`,
            value: selectedInstruction?.instruction.value ?? "",
            interval: "days",
            interval_count: daysSupply ?? 0,
          },
          dispense_quantity: dispenseQuantity,
        });
      },
    [category, daysSupply, handleVial],
  );

  const handleSupplies = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSupplyId(Number(event.target.value));
  }, []);

  const totalDispenseQuantity = getTotalDispenseQuantity(
    vials,
    category.name === "Weight Loss",
    dispenseQuantity,
    medication.form as MedicationForm,
  );

  const medIds = useMemo(
    () =>
      (isDispenseUnitVial
        ? vials.map((v) => v.medicationId)
        : Array(totalDispenseQuantity).fill(vials[0]?.medicationId)
      ).filter(isTruthy),

    [isDispenseUnitVial, totalDispenseQuantity, vials],
  );

  const instructions = useMemo(
    () => vials.map((v) => v.instruction).filter(isTruthy),
    [vials],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLoading(true);
      try {
        await updatePrescription({
          medication_ids: medIds,
          id: prescriptionId,
          dispense_quantity: quantity?.toString(),
          instructions: instructions,
          supply_id: supplyId,
          status: "prescribed",
          days_supply: durationInDays ?? 28,
          prescribed_at: new Date().toISOString(),
          refills: refills > 0 ? refills - 1 : 0,
          pharmacy_id: medication.pharmacyId,
        }).unwrap();

        await createOrder({
          prescription_id: prescriptionId,
          status: billingModel === "one_time" ? "paid" : "created",
          patient_id: patientId,
          pharmacy_id: medication.pharmacyId,
          product_id: productId,
          product_category_id: category.id,
          invoice_id: billingModel === "one_time" ? invoiceId : null,
          type: prescriptionType,
        }).unwrap();

        onSuccess();
        setShowAlert(true);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [
      billingModel,
      category.id,
      createOrder,
      durationInDays,
      instructions,
      invoiceId,
      medIds,
      medication.pharmacyId,
      onSuccess,
      patientId,
      prescriptionId,
      productId,
      quantity,
      refills,
      supplyId,
      updatePrescription,
    ],
  );

  const totalMedications = useMemo(() => {
    return ["oral", "dropper"].includes(medication.form || "")
      ? 1
      : dispenseQuantity;
  }, [dispenseQuantity, medication.form]);

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={500}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Prescription Request
          </Typography>
          <IconButton onClick={() => onClose(false)} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        {[...Array(totalMedications)].map((_, idx) => {
          const med = medicationOptions.find(
            (option) => option.id === vials[idx]?.medicationId,
          );

          return (
            <PrescriptionInputs
              key={idx}
              vials={vials}
              idx={idx}
              medication={medication}
              medicationOptions={medicationOptions}
              supplyId={supplyId}
              supplies={supplies}
              daysSupply={daysSupply}
              handleInstructions={handleInstruction}
              handleVial={handleVial}
              handleSupplies={handleSupplies}
              totalDispenseQuantity={totalDispenseQuantity}
              matchedMedicationOption={med}
            />
          );
        })}

        <Stack direction="row" gap={1} sx={{ mt: 3 }}>
          <FormControl sx={{ gap: 1 }} fullWidth>
            <FormLabel>Days supply:</FormLabel>
            <TextField required defaultValue={durationInDays ?? ""} disabled />
          </FormControl>

          <FormControl sx={{ gap: 1 }} fullWidth>
            <FormLabel>Dispense quantity:</FormLabel>
            <TextField required value={quantity ?? ""} disabled />
          </FormControl>

          {medication.dispense_unit ? (
            <FormControl sx={{ gap: 1 }} fullWidth>
              <FormLabel>Dispense unit:</FormLabel>
              <TextField
                required
                defaultValue={capitalize(medication.dispense_unit)}
                disabled
              />
            </FormControl>
          ) : null}
        </Stack>

        {showAlert ? (
          <Alert
            variant="filled"
            severity="success"
            sx={{ bgcolor: theme.palette.primary.main }}
          >
            Order was successfully sent to pharmacy, proceed to messaging
            patient.
          </Alert>
        ) : null}

        <Button type="submit" loading={loading} disabled={showAlert}>
          Send prescription
        </Button>
        <Button variant="alternate" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </Stack>
    </Modal>
  );
};

export default PrescribeModal;
